<template>
  <Teleport to="body">
    <canvas v-if="monkeys.isMonkeyEnabled" ref="canvas" class="monkey-canvas" aria-hidden="true" />
    <!-- Holding food or the stick, the page is out of reach until he lets go of it. -->
    <div
      v-if="toolInHand"
      class="monkey-tool-layer"
      @pointerdown="pressTool"
      @pointermove="moveTool"
      @pointerup="releaseTool"
      @pointercancel="cancelTool"
      @lostpointercapture="cancelTool"
      @contextmenu.prevent="selectTool('hand')"
    />
    <button
      v-if="sceneReady && interactionBounds"
      ref="monkeyHandle"
      type="button"
      class="monkey-handle"
      :class="{
        'monkey-handle-rotating': rotating,
        'monkey-handle-held': interacting,
        'monkey-handle-tool': toolInHand
      }"
      :style="handleStyle"
      aria-label="Aapje verplaatsen of draaien"
      :title="toolInHand ? undefined : handleTitle"
      @pointerdown="startInteraction"
      @pointermove="moveInteraction"
      @pointerup="endInteraction"
      @pointercancel="endInteraction"
      @lostpointercapture="endInteraction"
      @keydown="handleInteractionKey"
      @click.stop
      @contextmenu.prevent="selectTool('hand')"
    />

    <div
      v-if="toolInHand && toolCursor"
      :key="swingCount"
      class="monkey-tool-cursor"
      :class="[
        `monkey-tool-cursor-${tool}`,
        { 'monkey-tool-cursor-swing': tool === 'stick' && swingCount > 0 }
      ]"
      :style="{ left: `${toolCursor.x}px`, top: `${toolCursor.y}px` }"
      aria-hidden="true"
    >
      <MonkeyToolIcon v-if="tool !== 'hand'" :tool="tool" />
    </div>
    <span class="sr-only" aria-live="polite">{{ toolInHand ? currentTool.label : "" }}</span>
    <span
      v-for="impact in impacts"
      :key="impact.id"
      class="monkey-impact"
      :style="{ left: `${impact.x}px`, top: `${impact.y}px` }"
      aria-hidden="true"
    />

    <div class="monkey-controls" @keydown.esc="closePanel">
      <section
        v-if="panelOpen"
        id="peanuts-monkey-controls"
        ref="panel"
        aria-label="Aapje bedienen"
        class="surface monkey-panel border-gray-lighten-1/40 dark:border-gray-lighten-1-dark/40 rounded-xl border p-4 shadow-xl"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="text-sm font-semibold">Jouw aapje</h2>
            <p class="mt-0.5 text-xs opacity-60">Een eigen karakter</p>
          </div>
          <GeneralButton size="small" variant="text" color="primary" @click="closePanel">
            Sluiten
          </GeneralButton>
        </div>

        <p class="mt-3 text-xs leading-relaxed opacity-75">
          <template v-if="monkeys.isMonkeyRoaming">
            Hij loopt zelf rond; beweeg je muis rustig in zijn buurt om hem te lokken.
          </template>
          Sleep het aapje naar een andere plek. Ctrl + slepen draait hem. Draai het muiswiel boven
          het aapje en je muis wordt een nootje, een banaan of een stok; Esc of de rechtermuisknop
          legt het weer weg. Shift + muiswiel maakt hem groter of kleiner.
        </p>

        <template v-if="monkeys.isMonkeyEnabled">
          <p v-if="loadError" role="alert" class="text-error mt-3 text-xs">
            {{ loadError }}
          </p>
          <p v-else class="mt-3 flex items-center gap-2 text-xs" role="status">
            <span class="bg-secondary size-1.5 shrink-0 rounded-full" />
            {{ statusLabel }}
          </p>

          <label class="mt-3 flex flex-col gap-2 text-xs">
            <span>Grootte {{ Math.round(sizeScale * 100) }}%</span>
            <input
              type="range"
              min="50"
              max="200"
              step="5"
              :value="Math.round(sizeScale * 100)"
              :disabled="!sceneReady"
              class="accent-secondary min-h-8 w-full"
              @input="changeSize"
            />
          </label>

          <fieldset class="mt-3 text-xs">
            <legend class="mb-2">Bezigheid</legend>
            <div
              class="border-gray-lighten-1/40 dark:border-gray-lighten-1-dark/40 grid grid-cols-3 gap-0.5 rounded-lg border p-0.5"
            >
              <label
                v-for="option in activityOptions"
                :key="option.value"
                class="has-focus-visible:outline-secondary flex min-h-8 cursor-pointer items-center justify-center rounded-md px-2 font-medium transition-colors has-focus-visible:outline"
                :class="
                  monkeys.monkeyActivity === option.value
                    ? 'bg-secondary dark:bg-secondary-dark text-on-secondary dark:text-on-secondary-dark'
                    : 'hover:bg-gray-lighten-1/20 dark:hover:bg-gray-lighten-1-dark/20'
                "
              >
                <input
                  type="radio"
                  name="monkey-activity"
                  class="sr-only"
                  :value="option.value"
                  :checked="monkeys.monkeyActivity === option.value"
                  @change="monkeys.setMonkeyActivity(option.value)"
                />
                {{ option.label }}
              </label>
            </div>
            <p class="mt-1.5 opacity-60">{{ activityHint }}</p>
          </fieldset>

          <p v-if="reduceMotion && !motionAllowed" class="mt-3 text-xs leading-relaxed opacity-70">
            Je apparaat vraagt om minder beweging. Het aapje staat daarom stil.
          </p>
          <div
            class="border-gray-lighten-1/30 dark:border-gray-lighten-1-dark/30 mt-4 flex items-center justify-between gap-2 border-t pt-3"
          >
            <GeneralButton
              v-if="loadError"
              size="small"
              variant="text"
              color="secondary"
              @click="startScene"
            >
              Opnieuw proberen
            </GeneralButton>
            <GeneralButton
              v-else
              size="small"
              variant="text"
              color="secondary"
              :disabled="!sceneReady"
              @click="togglePause"
            >
              {{ movementPaused ? "Beweging toestaan" : "Pauzeren" }}
            </GeneralButton>
            <GeneralButton size="small" variant="text" color="primary" @click="disableMonkey">
              Aapje uitzetten
            </GeneralButton>
          </div>
        </template>
        <GeneralButton v-else color="secondary" class="mt-4 w-full" @click="enableMonkey">
          Aapje aanzetten
        </GeneralButton>
      </section>

      <GeneralButton
        ref="launcher"
        color="surface"
        variant="elevated"
        class="monkey-launcher border-gray-lighten-1/40! dark:border-gray-lighten-1-dark/40! gap-2 border!"
        :aria-expanded="panelOpen"
        aria-controls="peanuts-monkey-controls"
        aria-label="Aapje bedienen"
        @click="panelOpen = !panelOpen"
      >
        <span v-if="monkeys.isMonkeyEnabled" class="bg-secondary size-1.5 rounded-full" />
        <span>Aapje</span>
      </GeneralButton>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import MonkeyToolIcon from "@/components/mascot/MonkeyToolIcon.vue"
import { MonkeyMode } from "@/enums/monkey-modes"
import { useMonkeysStore, type MonkeyActivity } from "@/store/monkeys.store"
import type { createVendorMonkeyScene } from "@/mascot/monkey-vendor-scene"
import { capuchinProfile } from "@/mascot/monkey-capuchin-profile"
import { afterAppStartup } from "@/mascot/monkey-startup"
import { monkeyEvents } from "@/services/monkey-events-service"
import {
  createMonkeyToolWheel,
  distanceToMonkey,
  monkeyThrowVelocity,
  monkeyToolReach,
  monkeyTools,
  nextMonkeyTool,
  type MonkeyThrowSample,
  type MonkeyTool
} from "@/mascot/monkey-tools"

const monkeys = useMonkeysStore()
const activityOptions: { value: MonkeyActivity; label: string; hint: string }[] = [
  { value: "relax", label: "Relax", hint: "Hij loopt maar af en toe rond." },
  { value: "normal", label: "Normaal", hint: "Hij wisselt rondlopen en rusten af." },
  { value: "busy", label: "Druk", hint: "Hij is vrijwel de hele tijd op pad." }
]
const activityHint = computed(
  () => activityOptions.find((option) => option.value === monkeys.monkeyActivity)?.hint
)
const canvas = ref<HTMLCanvasElement>()
const panel = ref<HTMLElement>()
const launcher = ref<InstanceType<typeof GeneralButton>>()
const panelOpen = ref(false)
const sceneReady = ref(false)
const loadError = ref("")
const sceneLabel = ref("Rustig rondkijken")
const manualPaused = ref(false)
const reduceMotion = ref(false)
const motionAllowed = ref(false)
type InteractionBounds = { left: number; top: number; width: number; height: number }
const monkeyHandle = ref<HTMLButtonElement>()
const interactionBounds = shallowRef<InteractionBounds>()
const interacting = ref(false)
const rotating = ref(false)
const sizeScale = ref(1)
const handleStyle = computed(() =>
  interactionBounds.value
    ? {
        left: `${interactionBounds.value.left}px`,
        top: `${interactionBounds.value.top}px`,
        width: `${interactionBounds.value.width}px`,
        height: `${interactionBounds.value.height}px`
      }
    : undefined
)
let pointer: { id: number; x: number; y: number; element: HTMLButtonElement } | undefined
const handleTitle =
  "Sleep om te verplaatsen · Ctrl + slepen om te draaien · Muiswiel voor gereedschap · Shift + muiswiel voor de grootte"

// What the mouse pointer is: the wheel over him turns it into food or the stick.
const tool = ref<MonkeyTool>("hand")
const toolCursor = ref<{ x: number; y: number }>()
const swingCount = ref(0)
const impacts = ref<{ id: number; x: number; y: number }[]>([])
const toolWheel = createMonkeyToolWheel()
const currentTool = computed(() => monkeyTools.find((item) => item.id === tool.value)!)
const toolInHand = computed(() => tool.value !== "hand" && sceneReady.value)
let aim: { id: number; element: HTMLElement; samples: MonkeyThrowSample[] } | undefined
let impactCount = 0

function selectTool(next: MonkeyTool) {
  cancelTool()
  tool.value = next
  if (next === "hand") toolCursor.value = undefined
}

function sampleOf(event: PointerEvent): MonkeyThrowSample {
  return { x: event.clientX, y: event.clientY, time: performance.now() }
}

function nearMonkey(point: { x: number; y: number }) {
  const bounds = interactionBounds.value
  return !!bounds && distanceToMonkey(point, bounds) <= monkeyToolReach
}

/** Away from him, what the user held is gone and the mouse is the mouse again. */
function dropIfFar(point: { x: number; y: number }) {
  if (tool.value !== "hand" && !aim && !nearMonkey(point)) selectTool("hand")
}

function followCursor(event: PointerEvent) {
  if (tool.value === "hand" || event.pointerType !== "mouse") return
  toolCursor.value = { x: event.clientX, y: event.clientY }
  dropIfFar(toolCursor.value)
}

/** Using what he holds: tapping with the stick, or taking aim with the food. */
function pressTool(event: PointerEvent) {
  if (!toolInHand.value || aim || event.button !== 0 || !event.isPrimary) return
  event.preventDefault()
  event.stopPropagation()
  if (movementPaused.value) return
  const sample = sampleOf(event)
  toolCursor.value = { x: sample.x, y: sample.y }
  if (tool.value === "stick") {
    swingCount.value += 1
    const onMonkey =
      event.currentTarget instanceof Element && event.currentTarget.matches(".monkey-handle")
    if (onMonkey) {
      scene?.hit(sample)
      const id = ++impactCount
      impacts.value = [...impacts.value, { id, x: sample.x, y: sample.y }]
      setTimeout(() => (impacts.value = impacts.value.filter((item) => item.id !== id)), 450)
    } else scene?.poke(sample)
    return
  }
  const element = event.currentTarget as HTMLElement
  element.setPointerCapture?.(event.pointerId)
  aim = { id: event.pointerId, element, samples: [sample] }
}

function moveTool(event: PointerEvent) {
  if (!aim || event.pointerId !== aim.id) return
  aim.samples = [...aim.samples, sampleOf(event)].slice(-12)
  event.preventDefault()
  event.stopPropagation()
}

/** Letting go: a still hand puts the food down, a moving one throws it. */
function releaseTool(event: PointerEvent) {
  if (!aim || event.pointerId !== aim.id) return
  const sample = sampleOf(event)
  const samples = [...aim.samples, sample]
  cancelTool()
  if (tool.value === "peanut" || tool.value === "banana")
    scene?.giveTreat(tool.value, { x: sample.x, y: sample.y }, monkeyThrowVelocity(samples))
  // A throw may end further off; the food flies on, the hand is empty there.
  dropIfFar(sample)
  event.preventDefault()
  event.stopPropagation()
}

function cancelTool() {
  const previous = aim
  aim = undefined
  if (previous?.element.hasPointerCapture?.(previous.id))
    previous.element.releasePointerCapture(previous.id)
}

function onToolKey(event: KeyboardEvent) {
  if (event.key === "Escape" && tool.value !== "hand") selectTool("hand")
}

function finishInteraction() {
  const previous = pointer
  pointer = undefined
  interacting.value = false
  rotating.value = false
  if (previous?.element.hasPointerCapture(previous.id))
    previous.element.releasePointerCapture(previous.id)
  if (previous) scene?.setHeld(false)
}

function startInteraction(event: PointerEvent) {
  if (toolInHand.value) return pressTool(event)
  if (!sceneReady.value || pointer || event.button !== 0 || !event.isPrimary) return
  const element = event.currentTarget as HTMLButtonElement
  scene?.setHeld(true)
  element.setPointerCapture(event.pointerId)
  element.focus({ preventScroll: true })
  pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, element }
  interacting.value = true
  rotating.value = event.ctrlKey
  event.preventDefault()
  event.stopPropagation()
}

function moveInteraction(event: PointerEvent) {
  if (aim) return moveTool(event)
  if (!pointer || event.pointerId !== pointer.id) return
  const dx = event.clientX - pointer.x
  const dy = event.clientY - pointer.y
  pointer.x = event.clientX
  pointer.y = event.clientY
  rotating.value = event.ctrlKey
  if (event.ctrlKey) scene?.rotateBy(dx * 0.012)
  else scene?.moveBy(dx, dy)
  event.preventDefault()
  event.stopPropagation()
}

function endInteraction(event: PointerEvent) {
  if (aim) return event.type === "pointerup" ? releaseTool(event) : cancelTool()
  if (event.pointerId !== pointer?.id) return
  finishInteraction()
}

function changeSize(event: Event) {
  const requested = Number((event.target as HTMLInputElement).value) / 100
  if (sceneReady.value && Number.isFinite(requested))
    scene?.scaleBy(Math.max(0.5, Math.min(2, requested)) / sizeScale.value)
}

/**
 * The wheel near him turns the mouse into a tool and back; with Shift over him it sizes
 * him, with Ctrl it is browser zoom. Further off the wheel scrolls the page as ever.
 */
function wheelNearMonkey(event: WheelEvent) {
  if (!sceneReady.value || !scene || event.ctrlKey || event.metaKey) return
  const target = event.target instanceof Element ? event.target : undefined
  if (target?.closest(".monkey-controls")) return
  const point = { x: event.clientX, y: event.clientY }
  const onMonkey = !!target?.closest(".monkey-handle")
  if (event.shiftKey ? !onMonkey : !onMonkey && !nearMonkey(point)) return
  // Most browsers turn a Shift + wheel sideways.
  const raw = event.shiftKey ? event.deltaY || event.deltaX : event.deltaY
  if (!raw) return
  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1
  const delta = Math.max(-240, Math.min(240, raw * unit))
  event.preventDefault()
  event.stopPropagation()
  if (event.shiftKey) {
    scene.scaleBy(Math.exp(-delta * 0.0015))
    return
  }
  if (movementPaused.value) return
  // Scrolling by him is attention too: he stays with the mouse.
  scene.pointer({ ...point, time: performance.now() })
  const step = toolWheel.scroll(delta, performance.now())
  if (!step) return
  selectTool(nextMonkeyTool(tool.value, step))
  // The pointer becomes the new tool right where it is, before the mouse moves again.
  if (tool.value !== "hand") toolCursor.value = point
}

function handleInteractionKey(event: KeyboardEvent) {
  if (["+", "=", "-", "_"].includes(event.key) && !event.ctrlKey && !event.metaKey) {
    event.preventDefault()
    event.stopPropagation()
    if (sceneReady.value) scene?.scaleBy(event.key === "+" || event.key === "=" ? 1.1 : 1 / 1.1)
    return
  }
  if (event.key === "Escape") {
    event.stopPropagation()
    finishInteraction()
    return
  }
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    event.stopPropagation()
    panelOpen.value = true
    return
  }
  const deltas: Record<string, readonly [number, number]> = {
    ArrowLeft: [-20, 0],
    ArrowRight: [20, 0],
    ArrowUp: [0, -20],
    ArrowDown: [0, 20]
  }
  const delta = deltas[event.key]
  if (!delta || !sceneReady.value) return
  event.preventDefault()
  event.stopPropagation()
  if (event.ctrlKey) scene?.rotateBy((((delta[0] || -delta[1]) / 20) * Math.PI) / 12)
  else scene?.moveBy(delta[0], delta[1])
}

const movementPaused = computed(
  () => manualPaused.value || (reduceMotion.value && !motionAllowed.value)
)
const statusLabel = computed(() => {
  if (!sceneReady.value) return "Aapje laden…"
  if (movementPaused.value) return "Het aapje staat stil"
  return sceneLabel.value
})

let scene: ReturnType<typeof createVendorMonkeyScene> | undefined
let generation = 0
let mounted = false
let motionQuery: MediaQueryList | undefined

function resizeScene() {
  scene?.resize(window.innerWidth, window.innerHeight, Math.min(window.devicePixelRatio, 2))
  void updateControlsBounds()
}

async function updateControlsBounds() {
  await nextTick()
  if (!mounted || !scene) return
  const bounds = panelOpen.value ? panel.value?.getBoundingClientRect() : undefined
  scene.setControlsBounds(
    bounds
      ? { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height }
      : undefined
  )
}

function updatePause() {
  if (document.hidden || movementPaused.value) {
    finishInteraction()
    scene?.pointerLeave()
  }
  scene?.setPaused(document.hidden || movementPaused.value)
}

function observePointer(event: PointerEvent) {
  if (!sceneReady.value || pointer || movementPaused.value || document.hidden) return
  if (event.pointerType !== "mouse" || !event.isPrimary) return
  if (event.target instanceof Element && event.target.closest(".monkey-controls")) {
    scene?.pointerLeave()
    return
  }
  scene?.pointer({ x: event.clientX, y: event.clientY, time: performance.now() })
}

function clearPointer() {
  scene?.pointerLeave()
}

/** A click or tap anywhere but on the monkey or its controls: he may notice it. */
function observePress(event: PointerEvent) {
  if (!sceneReady.value || pointer || movementPaused.value || document.hidden) return
  if (!event.isPrimary || event.button !== 0) return
  if (event.target instanceof Element && event.target.closest(".monkey-controls")) return
  scene?.poke({ x: event.clientX, y: event.clientY, time: performance.now() })
}

function onWindowBlur() {
  finishInteraction()
  cancelTool()
  clearPointer()
}

function onCelebrate() {
  scene?.react("celebrate")
}

function onStartle() {
  scene?.react("startle")
}

function onMotionChange(event: MediaQueryListEvent) {
  reduceMotion.value = event.matches
  motionAllowed.value = false
}

function stopScene() {
  finishInteraction()
  selectTool("hand")
  interactionBounds.value = undefined
  generation += 1
  scene?.dispose()
  scene = undefined
  sceneReady.value = false
}

/** On page load he waits for the app to start first; switched on later, he comes at once. */
async function startScene(afterStartup = false) {
  stopScene()
  loadError.value = ""
  const request = generation
  await nextTick()
  if (!mounted || !monkeys.isMonkeyEnabled || !canvas.value) return

  try {
    if (afterStartup) await afterAppStartup()
    if (request !== generation || !mounted || !monkeys.isMonkeyEnabled) return
    const { createVendorMonkeyScene } = await import("@/mascot/monkey-vendor-scene")
    if (request !== generation || !mounted || !monkeys.isMonkeyEnabled || !canvas.value) return
    scene = createVendorMonkeyScene(canvas.value, capuchinProfile, {
      autonomous: true,
      // Half the renderer's reference size.
      heightPx: 90,
      // A pet with a routine of its own, as lively as the user set him.
      pet: { tempo: monkeys.monkeyActivity },
      onInteractionBounds(bounds) {
        if (request !== generation || !mounted) return
        interactionBounds.value = bounds
      },
      onSizeScaleChange(scale) {
        if (request !== generation || !mounted) return
        sizeScale.value = scale
      },
      onState(state) {
        if (request !== generation || !mounted) return
        sceneLabel.value = state.label
        if (state.completed === "eat") monkeys.registerFood(state.food ?? "banana")
      },
      onError(error) {
        if (request !== generation || !mounted) return
        console.error("Peanuts monkey scene could not render", error)
        sceneReady.value = false
        loadError.value = "Het aapje kon niet worden weergegeven. Probeer het opnieuw."
      }
    })
    scene.setRoaming(monkeys.isMonkeyRoaming)
    resizeScene()
    updatePause()
    await scene.ready
    if (request !== generation || !mounted || !monkeys.isMonkeyEnabled) return
    sceneReady.value = true
  } catch (error) {
    if (request !== generation || !mounted) return
    console.error("Peanuts monkey scene could not start", error)
    scene?.dispose()
    scene = undefined
    loadError.value = "Het aapje kon niet worden geladen. Probeer het opnieuw."
  }
}

function enableMonkey() {
  manualPaused.value = false
  motionAllowed.value = false
  monkeys.setMonkeyMode(MonkeyMode.FULL)
}

function disableMonkey() {
  monkeys.setMonkeyMode(MonkeyMode.OFF)
}

function togglePause() {
  if (movementPaused.value) {
    manualPaused.value = false
    motionAllowed.value = true
  } else {
    manualPaused.value = true
  }
}

function closePanel() {
  panelOpen.value = false
  launcher.value?.focus()
}

watch(
  () => monkeys.isMonkeyEnabled,
  (enabled) => {
    if (!mounted) return
    if (enabled) void startScene()
    else stopScene()
  },
  { flush: "post" }
)
// He may walk off from a still mouse; then it is just the mouse again.
watch(interactionBounds, () => {
  if (toolCursor.value) dropIfFar(toolCursor.value)
})
watch(movementPaused, (paused) => {
  updatePause()
  if (paused) selectTool("hand")
})
watch(
  () => monkeys.isMonkeyRoaming,
  (value) => scene?.setRoaming(value)
)
watch(
  () => monkeys.monkeyActivity,
  (value) => scene?.setTempo(value)
)
watch(
  [panelOpen, sceneReady, movementPaused, loadError, sceneLabel],
  () => void updateControlsBounds(),
  { flush: "post" }
)

onMounted(() => {
  mounted = true
  motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
  reduceMotion.value = motionQuery.matches
  motionQuery.addEventListener("change", onMotionChange)
  window.addEventListener("resize", resizeScene)
  window.addEventListener("blur", onWindowBlur)
  document.addEventListener("pointermove", observePointer, { passive: true })
  document.addEventListener("pointerleave", clearPointer)
  document.addEventListener("visibilitychange", updatePause)
  document.addEventListener("pointerdown", observePress, { passive: true })
  document.addEventListener("pointermove", followCursor, { passive: true })
  document.addEventListener("wheel", wheelNearMonkey, { passive: false })
  document.addEventListener("keydown", onToolKey)
  monkeyEvents.on("celebrate", onCelebrate)
  monkeyEvents.on("startle", onStartle)
  void updateControlsBounds()
  if (monkeys.isMonkeyEnabled) void startScene(true)
})

onBeforeUnmount(() => {
  mounted = false
  stopScene()
  motionQuery?.removeEventListener("change", onMotionChange)
  window.removeEventListener("resize", resizeScene)
  window.removeEventListener("blur", onWindowBlur)
  document.removeEventListener("pointermove", observePointer)
  document.removeEventListener("pointerleave", clearPointer)
  document.removeEventListener("visibilitychange", updatePause)
  document.removeEventListener("pointerdown", observePress)
  document.removeEventListener("pointermove", followCursor)
  document.removeEventListener("wheel", wheelNearMonkey)
  document.removeEventListener("keydown", onToolKey)
  monkeyEvents.off("celebrate", onCelebrate)
  monkeyEvents.off("startle", onStartle)
})
</script>

<style scoped>
.monkey-canvas {
  position: fixed;
  inset: 0;
  z-index: 45;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.monkey-handle {
  position: fixed;
  z-index: 45;
  padding: 0;
  border: 0;
  border-radius: 0.75rem;
  background: transparent;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

/* Never a frame around him: not after a drag, a key, or the window getting focus back. */
.monkey-handle,
.monkey-handle:focus,
.monkey-handle:focus-visible {
  outline: none;
  box-shadow: none;
}

.monkey-handle-tool {
  cursor: none;
}

.monkey-handle-rotating {
  cursor: ew-resize;
}

.monkey-handle-held:not(.monkey-handle-rotating) {
  cursor: grabbing;
}

.monkey-tool-layer {
  position: fixed;
  inset: 0;
  z-index: 44;
  cursor: none;
  touch-action: none;
}

/* The held item is the mouse pointer. A white rim keeps it clear on any background. */
.monkey-tool-cursor {
  position: fixed;
  z-index: 48;
  width: 2.75rem;
  height: 2.75rem;
  pointer-events: none;
  filter: drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff)
    drop-shadow(0 2px 2px rgb(0 0 0 / 0.35));
}

/* Food lands where its middle is. */
.monkey-tool-cursor-peanut,
.monkey-tool-cursor-banana {
  margin: -1.375rem 0 0 -1.375rem;
}

/* The stick hits with its tip, top left; it swings from the grip. */
.monkey-tool-cursor-stick {
  margin: -0.3rem 0 0 -0.3rem;
  transform-origin: 88% 88%;
}

.monkey-tool-cursor-swing {
  animation: monkey-swing 0.22s ease-out;
}

@keyframes monkey-swing {
  0% {
    transform: rotate(30deg);
  }
  45% {
    transform: rotate(-25deg);
  }
  100% {
    transform: rotate(0);
  }
}

.monkey-impact {
  position: fixed;
  z-index: 48;
  width: 1.75rem;
  height: 1.75rem;
  margin: -0.875rem 0 0 -0.875rem;
  pointer-events: none;
  background: radial-gradient(circle, #fff6 0 20%, #ffd54a 21% 38%, transparent 40%);
  clip-path: polygon(
    50% 0,
    61% 35%,
    98% 35%,
    68% 57%,
    79% 91%,
    50% 70%,
    21% 91%,
    32% 57%,
    2% 35%,
    39% 35%
  );
  animation: monkey-impact 0.45s ease-out forwards;
}

@keyframes monkey-impact {
  from {
    opacity: 1;
    transform: scale(0.4);
  }
  to {
    opacity: 0;
    transform: scale(1.5);
  }
}

@media (prefers-reduced-motion: reduce) {
  .monkey-tool-cursor-swing,
  .monkey-impact {
    animation-duration: 0.01s;
  }
}

.monkey-controls {
  position: fixed;
  right: max(1rem, env(safe-area-inset-right));
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 46;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  pointer-events: none;
}

.monkey-panel {
  width: min(19rem, calc(100vw - 2rem));
  max-height: calc(100dvh - 6rem);
  overflow-y: auto;
  pointer-events: auto;
}

.monkey-launcher {
  min-height: 2.5rem;
  padding-inline: 0.9rem;
  pointer-events: auto;
}
</style>
